import type { State } from '@src/redux/configureStore'
import * as MonitoringActionTypes from '@src/redux/actionTypes/monitoring.action.types'
import { defaultState } from '@src/redux/reducers/guardrails/monitoring/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import {
  MonitorInfo,
  IntentMgr,
  UpdateMonitorReq,
  CreateMonitorReq
} from '@src/endpoints/services/intentMgr.service'
import { toastSuccess } from '@src/utils/toast'
import { correctEmailFormat } from '@src/utils/accounts'
import uuidv4 from 'uuid/v4'
import { getAlertsCount } from '../dashboard.actions'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import { AppStorage } from '@src/utils/storage.helpers'
import { storagePrefix } from '@src/constants/constants'

const storage = new AppStorage(storagePrefix, sessionStorage)

// Action Creators
export type ActionTypes =
  | getMonitoringListSuccessAction
  | cleanerStatAction
  | setFlagOnlyEnabledGuardrailsAction
  | getMonitoringListFailAction
  | requestPendingAction
  | deleteMonitorSuccessAction
  | deleteMonitorFailAction
  | createMonitorSuccessAction
  | createMonitorFailAction
  | updateMonitorSuccessAction
  | updateMonitorFailAction

const getMonitoringListSuccess = (
  monitoringList: MonitorInfo[],
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.GET_MONITORING_LIST_SUCCESS,
    {
      monitoringList,
      isPending
    }
  )
type getMonitoringListSuccessAction = ReturnType<
  typeof getMonitoringListSuccess
>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  MonitoringActionTypes.CLEANER_STAT,
  defaultState
)

type cleanerStatAction = ReturnType<typeof cleanerStat>

export const setFlagOnlyEnabledGuardrails = (flag: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.SHOW_ONLY_ENABLED_GUARDRAILS,
    {
      flag
    }
  )

type setFlagOnlyEnabledGuardrailsAction = ReturnType<
  typeof setFlagOnlyEnabledGuardrails
>

const getMonitoringListFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.GET_MONITORING_LIST_FAIL,
    {
      error,
      isPending: false
    }
  )
type getMonitoringListFailAction = ReturnType<typeof getMonitoringListFail>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.REQUEST_PENDING,
    {
      isPending
    }
  )
type requestPendingAction = ReturnType<typeof requestPending>

const deleteMonitorSuccess = (id: string, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.DELETE_MONITOR_SUCCESS,
    {
      id,
      isPending
    }
  )
type deleteMonitorSuccessAction = ReturnType<typeof deleteMonitorSuccess>

const deleteMonitorFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.DELETE_MONITOR_FAIL,
    {
      error,
      isPending: false
    }
  )
type deleteMonitorFailAction = ReturnType<typeof deleteMonitorFail>

const createMonitorSuccess = (id: string, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.CREATE_MONITOR_SUCCESS,
    {
      id,
      isPending
    }
  )
type createMonitorSuccessAction = ReturnType<typeof createMonitorSuccess>

const createMonitorFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.CREATE_MONITOR_FAIL,
    {
      error,
      isPending: false
    }
  )
type createMonitorFailAction = ReturnType<typeof createMonitorFail>

const updateMonitorSuccess = (id: string, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.UPDATE_MONITOR_SUCCESS,
    {
      id,
      isPending
    }
  )
type updateMonitorSuccessAction = ReturnType<typeof updateMonitorSuccess>

const updateMonitorFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    MonitoringActionTypes.UPDATE_MONITOR_FAIL,
    {
      error,
      isPending: false
    }
  )
type updateMonitorFailAction = ReturnType<typeof updateMonitorFail>

export function getNormalizedMonitoringList(list: MonitorInfo[] = []) {
  const retList = list.map((item, index) => {
    if (typeof item.alerts_enabled === 'undefined') {
      item.alerts_enabled = false
    }
    if (typeof item.label === 'undefined') {
      item.label = 'Custom'
    }
    return item
  })
  return retList
}

export function cleaner() {
  return (dispatch: any, getState: () => State) => {
    dispatch(cleanerStat('monitorInfo'))
  }
}

export function getMonitoringList(callbackSuccess?: () => void) {
  return (dispatch: any, getState: () => State) => {
    dispatch(cleanerStat('monitorInfo'))
    dispatch(requestPending(true))
    IntentMgr.GetMonitor({ params: {} })
      .then(res =>
        defaultHandleStatus(res, getMonitoringListFail, true, dispatch)
      )
      .then(res => {
        const list = res.monitor_info_list || []
        const normalizedList = getNormalizedMonitoringList(list)
        dispatch(getMonitoringListSuccess(normalizedList))
        if (callbackSuccess) {
          callbackSuccess()
        }
      })
      .catch((res: Response) =>
        dispatch(getMonitoringListFail(res?.statusText))
      )
  }
}

interface PatchMonitorsOptions {
  monitorIds: string[]
  enable: boolean
}

interface PatchMonitorsParams {
  monitor_ids?: string[]
  alerts_enabled?: boolean
  req_hdr?: {
    tid: string
  }
}

export function patchMonitors(
  { monitorIds, enable }: PatchMonitorsOptions,
  useAlertV2?: boolean,
  callback?: () => void
) {
  const params: PatchMonitorsParams = {
    monitor_ids: monitorIds,
    alerts_enabled: enable
  }
  params.req_hdr = {
    tid: uuidv4()
  }
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    IntentMgr.UpdateMonitor({ params })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestPending(false))
          },
          true
        )
      )
      .then(res => {
        dispatch(requestPending(false))
        dispatch(getMonitoringList())
        if (callback) callback()
        dispatch(getAlertsCount({ useV2: useAlertV2 }))
      })
      .catch((res: Response) => dispatch(requestPending(false)))
  }
}

export function deleteMonitor({
  id,
  useAlertV2
}: {
  id: string
  useAlertV2: boolean
}) {
  const params = { monitor_id: id }
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    IntentMgr.DeleteMonitor({ params })
      .then(res => defaultHandleStatus(res, deleteMonitorFail, true, dispatch))
      .then(res => {
        toastSuccess('Guardrail deleted.')
        dispatch(deleteMonitorSuccess(res as any))
        dispatch(getMonitoringList())
        dispatch(getAlertsCount({ useV2: useAlertV2 }))
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(deleteMonitorFail(res?.statusText)))
  }
}

export interface UpdateMonitorOptions extends UpdateMonitorReq {}

export function updateMonitor(
  options: UpdateMonitorOptions,
  useAlertV2: boolean,
  callbackSuccess?: () => void
) {
  const userEmail = storage.get('userEmail')
  const params = options || ({} as UpdateMonitorOptions)
  const { name } = params

  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    IntentMgr.UpdateMonitor({
      params: {
        req_hdr: {
          tid: uuidv4()
        },
        ...params,
        label: params?.label || 'Custom',
        user_id: correctEmailFormat(userEmail)
      }
    })
      .then(res => defaultHandleStatus(res, updateMonitorFail, true, dispatch))
      .then(res => {
        const message = 'Guardrail ' + name + ' updated.'
        if (callbackSuccess) {
          callbackSuccess()
        }
        toastSuccess(message)
        dispatch(updateMonitorSuccess(res as any))
        dispatch(getMonitoringList())
        dispatch(getAlertsCount({ useV2: useAlertV2 }))
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(updateMonitorFail(res?.statusText)))
  }
}

export interface CreateMonitorOptions extends CreateMonitorReq {}

export function createMonitor(
  options: CreateMonitorOptions,
  useAlertV2: boolean,
  callbackSuccess: () => void
) {
  const params = options || ({} as CreateMonitorOptions)
  const userEmail = storage.get('userEmail')
  const { name } = params

  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    IntentMgr.CreateMonitor({
      params: {
        req_hdr: {
          tid: uuidv4()
        },
        ...params,
        user_id: correctEmailFormat(userEmail),
        label: params?.label || 'Custom'
      }
    })
      .then(res => defaultHandleStatus(res, createMonitorFail, true, dispatch))
      .then(res => {
        const message = 'Guardrail ' + name + ' created.'
        if (callbackSuccess) {
          callbackSuccess()
        }
        toastSuccess(message)
        dispatch(createMonitorSuccess(res as any))
        dispatch(getAlertsCount({ useV2: useAlertV2 }))
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(createMonitorFail(res?.statusText)))
  }
}
